/**
 * RecipeDetail Component
 * 
 * Purpose: Full recipe view with ingredients, instructions, and share functionality
 * Features: Large hero photo, rating, cook time, tags, ingredients, instructions, share link
 */

import React, { useState, useEffect } from 'react';
import config from '../config/environments';

const API_URL = config.api.baseUrl;

const RecipeDetail = ({ recipeId, recipe: initialRecipe, onBack }) => {
  const [recipe, setRecipe] = useState(initialRecipe);
  const [isLoading, setIsLoading] = useState(!initialRecipe);
  const [error, setError] = useState(null);
  const [shareSuccess, setShareSuccess] = useState(false);

  // Fetch recipe if not provided
  useEffect(() => {
    if (!initialRecipe && recipeId) {
      fetchRecipe();
    }
  }, [recipeId, initialRecipe]);

  const fetchRecipe = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/recipes/${recipeId}`);
      
      if (!response.ok) {
        throw new Error('Recipe not found');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setRecipe(data.recipe);
      } else {
        setError(data.message || 'Failed to load recipe');
      }
    } catch (err) {
      console.error('Error fetching recipe:', err);
      setError('Could not load recipe. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Format cook time
  const formatTime = (minutes) => {
    if (!minutes) return 'Not specified';
    
    if (minutes < 60) {
      return `${minutes} minutes`;
    } else {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (mins === 0) {
        return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
      }
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${mins} minutes`;
    }
  };

  // Render star rating
  const renderStars = (rating) => {
    if (!rating) return null;
    
    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, i) => (
          <svg
            key={i}
            className={`w-6 h-6 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        <span className="ml-2 text-lg font-semibold text-gray-700">{rating}.0 / 5.0</span>
      </div>
    );
  };

  // Share recipe
  const shareRecipe = async () => {
    const shareUrl = `${window.location.origin}/recipes/${recipe.id}`;
    
    try {
      // Try native share API first (mobile)
      if (navigator.share) {
        await navigator.share({
          title: recipe.name,
          text: `Check out this recipe: ${recipe.name}`,
          url: shareUrl
        });
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(shareUrl);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error sharing:', err);
      // Try clipboard as final fallback
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 3000);
      } catch (clipboardErr) {
        console.error('Clipboard error:', clipboardErr);
      }
    }
  };

  // Share on WhatsApp
  const shareOnWhatsApp = () => {
    const shareUrl = `${window.location.origin}/recipes/${recipe.id}`;
    const message = `Check out this amazing recipe: ${recipe.name}! ${shareUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  // Parse ingredients (handle both string and object formats)
  const parseIngredients = () => {
    if (!recipe.ingredients) return [];
    
    try {
      // If it's a string, parse it
      if (typeof recipe.ingredients === 'string') {
        return JSON.parse(recipe.ingredients);
      }
      // If it's already an array, return it
      return recipe.ingredients;
    } catch (e) {
      console.error('Error parsing ingredients:', e);
      return [];
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">👨‍🍳</div>
          <h2 className="text-2xl font-bold text-gray-800">Loading Recipe...</h2>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !recipe) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center p-4">
        <div className="max-w-md mx-auto text-center bg-white rounded-xl p-8 shadow-lg">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Recipe Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'This recipe could not be loaded.'}</p>
          <button
            onClick={onBack}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-pink-600 transition-all"
          >
            Back to Recipe Book
          </button>
        </div>
      </div>
    );
  }

  const ingredients = parseIngredients();
  const imageUrl = recipe.photo_url ? `${API_URL}${recipe.photo_url}` : 'https://via.placeholder.com/800x500?text=Recipe+Photo';

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
      {/* Hero Image Section */}
      <div className="relative h-96 bg-gray-900 overflow-hidden">
        <img
          src={imageUrl}
          alt={recipe.name}
          className="w-full h-full object-cover opacity-90"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/800x500?text=Recipe+Photo';
          }}
        />
        
        {/* Overlay with gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        
        {/* Back button */}
        <button
          onClick={onBack}
          className="absolute top-6 left-6 p-3 bg-white/90 backdrop-blur rounded-full hover:bg-white transition-all shadow-lg"
        >
          <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Share buttons */}
        <div className="absolute top-6 right-6 flex gap-3">
          <button
            onClick={shareRecipe}
            className="p-3 bg-white/90 backdrop-blur rounded-full hover:bg-white transition-all shadow-lg relative"
          >
            <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            {shareSuccess && (
              <div className="absolute -bottom-12 right-0 bg-green-500 text-white px-3 py-1 rounded-lg text-sm font-medium whitespace-nowrap">
                Link copied!
              </div>
            )}
          </button>
          
          <button
            onClick={shareOnWhatsApp}
            className="p-3 bg-green-500 backdrop-blur rounded-full hover:bg-green-600 transition-all shadow-lg"
          >
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.886 3.75"/>
            </svg>
          </button>
        </div>

        {/* Recipe title at bottom of hero */}
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-2 drop-shadow-lg">
            {recipe.name}
          </h1>
          {recipe.description && (
            <p className="text-lg text-white/90 max-w-3xl drop-shadow">
              {recipe.description}
            </p>
          )}
        </div>
      </div>

      {/* Recipe Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Recipe Info Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-md text-center">
                <div className="text-3xl mb-2">⏱️</div>
                <div className="text-sm text-gray-600 mb-1">Cook Time</div>
                <div className="font-bold text-gray-800">{formatTime(recipe.cook_time_minutes)}</div>
              </div>
              
              {recipe.prep_time_minutes && (
                <div className="bg-white rounded-xl p-4 shadow-md text-center">
                  <div className="text-3xl mb-2">🔪</div>
                  <div className="text-sm text-gray-600 mb-1">Prep Time</div>
                  <div className="font-bold text-gray-800">{formatTime(recipe.prep_time_minutes)}</div>
                </div>
              )}
              
              <div className="bg-white rounded-xl p-4 shadow-md text-center">
                <div className="text-3xl mb-2">👥</div>
                <div className="text-sm text-gray-600 mb-1">Servings</div>
                <div className="font-bold text-gray-800">{recipe.servings || 4}</div>
              </div>
              
              {recipe.admin_rating && (
                <div className="bg-white rounded-xl p-4 shadow-md text-center">
                  <div className="text-3xl mb-2">⭐</div>
                  <div className="text-sm text-gray-600 mb-1">Rating</div>
                  <div className="font-bold text-gray-800">{recipe.admin_rating}.0 / 5.0</div>
                </div>
              )}
            </div>

            {/* Ingredients Section */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
                <span className="mr-3">🥘</span>
                Ingredients
              </h2>
              
              <ul className="space-y-3">
                {ingredients.map((ingredient, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-3 mt-1">✓</span>
                    <span className="text-gray-700">
                      {ingredient.quantity && ingredient.unit && (
                        <span className="font-semibold text-gray-900">
                          {ingredient.quantity} {ingredient.unit}{' '}
                        </span>
                      )}
                      {ingredient.name}
                    </span>
                  </li>
                ))}
              </ul>

              {ingredients.length === 0 && (
                <p className="text-gray-500 italic">No ingredients listed</p>
              )}
            </div>

            {/* Instructions Section */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
                <span className="mr-3">📋</span>
                Instructions
              </h2>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {recipe.instructions}
                </p>
              </div>
            </div>

            {/* Admin Notes/Review */}
            {recipe.admin_notes && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-8 shadow-lg border-2 border-purple-200">
                <h3 className="text-2xl font-bold text-purple-900 mb-4 flex items-center">
                  <span className="mr-3">💭</span>
                  Chef's Notes
                </h3>
                <p className="text-purple-800 italic leading-relaxed">
                  "{recipe.admin_notes}"
                </p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Tags */}
            {recipe.tags && recipe.tags.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Categories</h3>
                <div className="flex flex-wrap gap-2">
                  {recipe.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-2 bg-gradient-to-r from-orange-100 to-pink-100 text-orange-800 text-sm font-medium rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Source Link */}
            {recipe.source_url && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Original Source</h3>
                <a
                  href={recipe.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center px-4 py-3 bg-gradient-to-r from-pink-500 to-red-500 text-white rounded-lg font-semibold hover:from-pink-600 hover:to-red-600 transition-all"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  View on Instagram
                </a>
              </div>
            )}

            {/* Share Section */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 shadow-lg border-2 border-green-200">
              <h3 className="text-xl font-bold text-green-900 mb-4">Share This Recipe</h3>
              <p className="text-green-800 text-sm mb-4">
                Love this recipe? Share it with friends and family!
              </p>
              <div className="space-y-3">
                <button
                  onClick={shareRecipe}
                  className="w-full px-4 py-3 bg-white hover:bg-gray-50 text-gray-800 rounded-lg font-medium transition-all border-2 border-gray-200 flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Copy Link
                </button>
                <button
                  onClick={shareOnWhatsApp}
                  className="w-full px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-all flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.886 3.75"/>
                  </svg>
                  Share on WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeDetail;

